import type { Brand } from "@staffsmith/api-types";
import { importPage, PRODUCT_NAME } from "@staffsmith/web-auth";
import { useQuery } from "@tanstack/react-query";
import { Navigate, NavLink, Outlet, type RouteObject, useNavigate } from "react-router";
import { ApiError } from "./api.js";
import { useServices } from "./context.js";
import { SignInPage } from "./pages/SignIn.js";
import { ErrorNote, Loading } from "./ui.js";

// One portal for both kinds of portal user: a consultant lands on their time, a
// client contact on their shortlist (and their company's timesheets). Each page
// loads only when first visited (ADR 0003: route-level code splitting).
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await importPage(load))?.default ?? (() => null),
});

// The staffing firm's own mark beside the product name (and beside, never
// instead of, the Demo tag). The logo is drawn only as an image, so nothing in it runs.
function FirmMark({ brand, name }: { brand: Brand; name: string }) {
  return (
    <span className="firm-mark">
      {brand.logo_svg ? (
        <img
          src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(brand.logo_svg)}`}
          alt=""
          height={22}
        />
      ) : null}
      <span>{brand.short_name ?? name}</span>
    </span>
  );
}

function Layout() {
  const { api, auth } = useServices();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, enabled: auth.isSignedIn() });
  if (!auth.isSignedIn()) return <Navigate to="/sign-in" replace />;
  const noAccess = me.error instanceof ApiError && me.error.status === 403;
  const brand = me.data?.brand;
  return (
    <div
      className="shell"
      style={
        brand?.primary_color
          ? ({ "--brand-primary": brand.primary_color } as React.CSSProperties)
          : undefined
      }
    >
      <nav className={brand?.primary_color ? "nav branded" : "nav"} aria-label="Main">
        <span className="brand">{PRODUCT_NAME}</span>
        {brand ? <FirmMark brand={brand} name={me.data?.firm_name ?? ""} /> : null}
        {me.data?.demo ? (
          <span className="badge demo" title="Synthetic data only">
            Demo
          </span>
        ) : null}
        {me.data?.kind === "client_contact" ? (
          <>
            <NavLink to="/shortlist">Shortlist</NavLink>
            <NavLink to="/approvals">Timesheets</NavLink>
          </>
        ) : null}
        {me.data?.kind === "consultant" ? (
          <>
            <NavLink to="/getting-started">Getting started</NavLink>
            <NavLink to="/time">Your time</NavLink>
            <NavLink to="/pay">Your pay</NavLink>
            <NavLink to="/skills">Your skills</NavLink>
            <NavLink to="/privacy">Your privacy</NavLink>
          </>
        ) : null}
        <span className="spacer" />
        <span className="muted">{me.data?.name ?? ""}</span>
        <button
          type="button"
          className="link"
          onClick={() => {
            auth.signOut();
            void navigate("/sign-in");
          }}
        >
          Sign out
        </button>
      </nav>
      <main>
        {me.isPending ? <Loading /> : null}
        {noAccess ? (
          <p className="notice">
            This sign-in has no portal access. Ask your {PRODUCT_NAME} contact.
          </p>
        ) : (
          <ErrorNote error={me.error} />
        )}
        {me.data ? <Outlet /> : null}
      </main>
    </div>
  );
}

function Home() {
  const { api } = useServices();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  if (!me.data) return null;
  return <Navigate to={me.data.kind === "consultant" ? "/time" : "/shortlist"} replace />;
}

export const routes: RouteObject[] = [
  { path: "/sign-in", element: <SignInPage /> },
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Home /> },
      { path: "time", lazy: page(() => import("./pages/Time.js")) },
      { path: "getting-started", lazy: page(() => import("./pages/Onboarding.js")) },
      { path: "pay", lazy: page(() => import("./pages/Pay.js")) },
      { path: "privacy", lazy: page(() => import("./pages/Privacy.js")) },
      { path: "skills", lazy: page(() => import("./pages/Skills.js")) },
      { path: "shortlist", lazy: page(() => import("./pages/Shortlist.js")) },
      { path: "approvals", lazy: page(() => import("./pages/Approvals.js")) },
    ],
  },
];
